import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import { toAmericanOdds } from "../utils/odds";
import { formatKickoff as fmtKickoff } from "../utils/fixtureStatus";
import { MatchupHistoryContent } from "../components/MatchupHistoryModal";
import { getNflMatchupHistory, getNflStandings } from "../api/nflSurvivor";
import {
  getMyPickemPicks,
  getPickemLeaderboard,
  getPickemWeek,
  savePickemPick,
} from "../api/nflPickem";
import { useAuth } from "../auth/AuthContext";
import { useNflSeason } from "../hooks/useNflSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const CONFIDENCE_STEPS = [1, 2, 3, 4, 5];
const MAX_WEEK = 18;

// Highlight for a made pick — the team name and the confidence badge share
// this so they read as one thing.
const PICK_HIGHLIGHT_BG = "yellow.100";
const PICK_HIGHLIGHT_FG = "yellow.800";


/**
 * How a pick's result reads in the history grid.
 *
 * Once scored it shows the points won or lost, signed and coloured; before
 * that it falls back to the confidence staked, so an upcoming week still shows
 * what is riding on it.
 */
function historyResult(item, neutralColor) {
  const pts = item.points_awarded;
  if (pts == null) {
    return {
      label: item.confidence_points != null ? ` (${item.confidence_points})` : "",
      color: neutralColor,
    };
  }
  if (pts > 0) return { label: ` (+${pts})`, color: "green.600" };
  if (pts < 0) return { label: ` (${pts})`, color: "red.600" };
  return { label: " (0)", color: neutralColor };
}


/**
 * One selectable team inside the pick modal.
 *
 * `blockedReason` is why the team can't be picked — already used, or picked
 * against too often. Showing the reason on the button saves the player
 * clicking it to find out.
 */
function TeamChoice({ team, side, selected, blockedReason, onSelect }) {
  const disabled = !!blockedReason;
  return (
    <Box flex="1" minW={{ base: "100%", md: "180px" }}>
      <Text fontSize="0.6rem" color="gray.500" textTransform="uppercase" mb={1}>
        {side}
      </Text>
      <Button
        w="100%"
        variant={selected ? "solid" : "outline"}
        bg={selected ? TOP_BAR_BLUE : "white"}
        color={selected ? "white" : TOP_BAR_BLUE}
        borderColor={TOP_BAR_BLUE}
        onClick={disabled ? undefined : onSelect}
        isDisabled={disabled}
      >
        <HStack spacing={2} justify="center" minW="0">
          {team?.logo_url && (
            <Image src={team.logo_url} boxSize="20px" objectFit="contain" flexShrink={0} />
          )}
          <Text fontSize="sm" noOfLines={1}>{team?.name}</Text>
        </HStack>
      </Button>
      {blockedReason && (
        <Text fontSize="0.6rem" color="red.600" lineHeight="1.2" mt={1}>
          {blockedReason}
        </Text>
      )}
    </Box>
  );
}

export default function NflPickemPicksPage() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { season, seasonId, loading: seasonLoading } = useNflSeason();
  const effectiveSeasonId = location.state?.seasonId ?? seasonId;

  const [week, setWeek] = useState(1);
  // Open on the week actually being played rather than always Week 1, which
  // showed every game locked and read as "picks are closed". `current_week` is
  // the earliest week that isn't fully final, so a player arriving on Sunday
  // still lands on the week whose late games they can pick. Seeded once, so
  // the arrows keep working afterwards.
  const [weekSeeded, setWeekSeeded] = useState(false);
  useEffect(() => {
    if (weekSeeded || seasonLoading) return;
    const wk = Number(season?.current_week);
    if (wk >= 1) setWeek(wk);
    setWeekSeeded(true);
  }, [season, seasonLoading, weekSeeded]);
  const [weekData, setWeekData] = useState(null);
  const [standings, setStandings] = useState(null);
  const [board, setBoard] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [activeGame, setActiveGame] = useState(null);
  const [modalTeamId, setModalTeamId] = useState(null);
  const [modalConfidence, setModalConfidence] = useState(3);
  const [modalUnderdog, setModalUnderdog] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const shortTitle = useBreakpointValue({ base: true, md: false }) ?? false;

  const load = useCallback(async () => {
    if (!effectiveSeasonId || !poolId) return;
    setLoading(true);
    setError("");
    try {
      const [w, lb, mine] = await Promise.all([
        getPickemWeek(effectiveSeasonId, poolId, week),
        getPickemLeaderboard(effectiveSeasonId, poolId).catch(() => null),
        getMyPickemPicks(effectiveSeasonId, poolId).catch(() => ({ picks: [] })),
      ]);
      setWeekData(w);
      setBoard(lb);
      setHistory(mine?.picks || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load this week.");
    } finally {
      setLoading(false);
    }
  }, [effectiveSeasonId, poolId, week]);

  useEffect(() => {
    // Waiting on the seed keeps the page from fetching Week 1 and then
    // immediately refetching the week it actually wants.
    if (!seasonLoading && weekSeeded) load();
  }, [load, seasonLoading, weekSeeded]);

  useEffect(() => {
    if (!effectiveSeasonId) return;
    getNflStandings(effectiveSeasonId).then(setStandings).catch(() => setStandings(null));
  }, [effectiveSeasonId]);

  const pick = weekData?.pick || null;
  const avail = weekData?.availability || {};
  const usedSet = useMemo(() => new Set(avail.teams_used || []), [avail.teams_used]);

  const underdogUsesLeft = Math.max(
    0, (avail.underdog_uses_total || 0) - (avail.underdog_uses_spent || 0)
  );

  /** Points the selected team is getting, or null when no spread is published. */
  const underdogPoints = (() => {
    const pts = avail.underdog_points || {};
    if (!modalTeamId) return null;
    const v = pts[modalTeamId] ?? pts[String(modalTeamId)];
    return v === undefined ? null : v;
  })();

  /**
   * Why the Underdog Bonus can't be used on this pick, or "" if it can.
   *
   * Checked per team against the pool's threshold, mirroring the server's
   * underdog_qualifies() so the control never offers something the save would
   * refuse. Spreads arrive 1-7 days before kickoff, so a game further out
   * legitimately has none yet.
   */
  const underdogBlockedReason = (() => {
    if (underdogUsesLeft <= 0 && !modalUnderdog) {
      return `You've used all ${avail.underdog_uses_total} of your Underdog Bonuses.`;
    }
    if (!modalTeamId) return "Pick a team first.";
    if (!avail.underdog_spreads_available || underdogPoints === null) {
      return "No point spread published for this game yet.";
    }
    const threshold = avail.underdog_threshold ?? 4.5;
    if (underdogPoints <= 0) {
      return "The Underdog Bonus is only for underdogs.";
    }
    if (underdogPoints < threshold) {
      return `This team is only getting ${underdogPoints} points; the bonus needs ${threshold} or more.`;
    }
    return "";
  })();
  const atLimitSet = useMemo(
    () => new Set(avail.teams_at_against_limit || []),
    [avail.teams_at_against_limit]
  );

  /**
   * Why a team can't be picked, or null if it can.
   *
   * Picking a team means its opponent absorbs a pick-against, so the limit is
   * checked on the opponent rather than on the team being chosen.
   */
  const blockedReason = useCallback(
    (game, teamId) => {
      if (!teamId) return null;
      if (usedSet.has(teamId)) return "Already used";
      const opponentId =
        teamId === game.home_team?.id ? game.away_team?.id : game.home_team?.id;
      if (atLimitSet.has(opponentId)) {
        return `Picked against ${avail.pick_against_limit}x`;
      }
      return null;
    },
    [usedSet, atLimitSet, avail.pick_against_limit]
  );

  function openGame(game) {
    if (game.is_locked) return;
    setActiveGame(game);
    setModalTeamId(pick?.game_id === game.id ? pick.picked_team_id : null);
    setModalConfidence(pick?.game_id === game.id ? pick.confidence_points : 3);
    setModalUnderdog(pick?.game_id === game.id ? !!pick.used_underdog_bonus : false);
    onOpen();
  }

  async function handleSave() {
    if (!activeGame || !modalTeamId) return;
    setSaving(true);
    setError("");
    try {
      await savePickemPick(effectiveSeasonId, poolId, week, {
        game_id: activeGame.id,
        team_id: modalTeamId,
        confidence_points: modalConfidence,
        use_underdog_bonus: modalUnderdog,
      });
      onClose();
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save pick.");
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const teamById = useMemo(() => {
    const m = {};
    (weekData?.games || []).forEach((g) => {
      if (g.home_team) m[g.home_team.id] = g.home_team;
      if (g.away_team) m[g.away_team.id] = g.away_team;
    });
    return m;
  }, [weekData]);

  const pickedTeam = pick ? teamById[pick.picked_team_id] : null;

  const username = user?.username || user?.email || "Player";

  // Every week of the season, so unpicked weeks still show as gaps rather
  // than the list silently skipping them.
  const historyByWeek = useMemo(() => {
    const byWeek = {};
    history.forEach((p) => { byWeek[p.week] = p; });
    return Array.from({ length: MAX_WEEK }, (_, i) => {
      const wk = i + 1;
      return byWeek[wk] || {
        week: wk, team: null, confidence_points: null,
        points_awarded: null, is_auto_pick: false,
      };
    });
  }, [history]);
  const myRow = useMemo(
    () => (board?.rows || []).find((r) => r.user_id === user?.id) || null,
    [board, user]
  );

  // NFL has no single league table, so the panel lists the eight divisions.
  const divisionGroups = useMemo(() => {
    const out = [];
    (standings?.conferences || []).forEach((c) =>
      (c.divisions || []).forEach((d) =>
        out.push({ key: `${c.conference} ${d.division}`, teams: d.teams || [] })
      )
    );
    return out;
  }, [standings]);

  // The endpoint groups by conference then division; the modal only needs to
  // look a team up by id.
  const recordByTeam = useMemo(() => {
    const m = {};
    (standings?.conferences || []).forEach((c) =>
      (c.divisions || []).forEach((d) =>
        (d.teams || []).forEach((row) => {
          if (row.team?.id) m[row.team.id] = row;
        })
      )
    );
    return m;
  }, [standings]);

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="NFL PICK 'EM PICKS"
        user={user}
        onLogout={handleLogout}
        onBack={() => navigate(`/nfl-pickem/current/${poolId}`, { state: { seasonId: effectiveSeasonId } })}
      />

      <Container maxW="container.xl" py={{ base: 5, md: 8 }}>
        <VStack align="stretch" spacing={5}>
          <Flex justify="flex-start">
            <Button
              size="sm"
              bg="white"
              color={TOP_BAR_BLUE}
              _hover={{ bg: "gray.100" }}
              onClick={() => navigate(`/nfl-pickem/current/${poolId}`,
                                      { state: { seasonId: effectiveSeasonId } })}
            >
              Return to Leaderboard
            </Button>
          </Flex>

          <Flex align="stretch" gap={{ base: 3, md: 6 }} flexWrap="wrap">
            <Box flex="2" minW={{ base: "100%", md: "360px" }} border="2px solid white"
                 borderRadius="lg" px={5} py={4} bg="whiteAlpha.50">
              <Text fontFamily="'Orbitron', sans-serif"
                    fontSize={{ base: "18px", md: "30px" }}
                    letterSpacing="2px" fontWeight="600" color="white">
                {username}
              </Text>
            </Box>

            <Box flex="1" minW={{ base: "100%", md: "360px" }}
                 maxW={{ base: "100%", md: "360px" }}>
              <Grid templateColumns="repeat(3, 1fr)" gap={4} h="100%">
                {[
                  { label: "Points", value: myRow?.points ?? 0 },
                  { label: "PD", value: myRow?.margin ?? 0 },
                  { label: "Rank", value: myRow?.rank ?? "-" },
                ].map(({ label, value }) => (
                  <Box key={label} border="2px solid white" borderRadius="lg"
                       px={4} py={3} bg="whiteAlpha.50" textAlign="center">
                    <Text fontSize="xs" color="whiteAlpha.800" textTransform="uppercase">
                      {label}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">
                      {value}
                    </Text>
                  </Box>
                ))}
              </Grid>
            </Box>
          </Flex>

          {error && (
            <Alert status="error" borderRadius="md" color="red.900">
              <AlertIcon />{error}
            </Alert>
          )}

          {pick && (
            <Alert status="success" borderRadius="md" color="green.900">
              <AlertIcon />
              <Text>
                Week {week}: <strong>{pickedTeam?.name || "Pick saved"}</strong>{" "}
                for <strong>{pick.confidence_points}</strong> point
                {pick.confidence_points === 1 ? "" : "s"}
                {pick.is_auto_pick ? " (autopick)" : ""}
              </Text>
            </Alert>
          )}

          <Flex align="stretch" gap={{ base: 3, md: 6 }} flexWrap="wrap">
          <Box flex="2" minW={{ base: "100%", md: "600px" }}
               maxW={{ base: "100%", md: "calc(100% - 384px)" }}
               bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mb={4}>
              <HStack spacing={3}>
                <Button size="sm" onClick={() => setWeek(week - 1)}
                        isDisabled={week <= 1 || loading}>
                  ◀
                </Button>
                <Heading size="md" color={TOP_BAR_BLUE}>Week {week} Pick</Heading>
                <Button size="sm" onClick={() => setWeek(week + 1)}
                        isDisabled={week >= MAX_WEEK || loading}>
                  ▶
                </Button>
              </HStack>
              <Text fontWeight="bold" color={TOP_BAR_BLUE}>
                Select Matchup to Make Pick
              </Text>
            </Flex>

            {loading ? (
              <Flex justify="center" py={10}><Spinner /></Flex>
            ) : (
              <>
                <Flex flexWrap="wrap" gap={{ base: 3, md: 4 }}>
                  {(weekData?.games || []).map((g) => {
                    const isPicked = pick?.game_id === g.id;
                    return (
                      <Box
                        key={g.id}
                        as="button"
                        type="button"
                        onClick={() => openGame(g)}
                        disabled={g.is_locked}
                        textAlign="left"
                        w={{ base: "calc(50% - 6px)", md: "calc(25% - 12px)" }}
                        borderWidth="3px"
                        borderColor={isPicked ? "#d69e2e" : TOP_BAR_BLUE}
                        borderRadius="md"
                        bg="white"
                        boxShadow={isPicked ? "md" : "sm"}
                        p={{ base: 2, md: 3 }}
                        opacity={g.is_locked && !isPicked ? 0.5 : 1}
                        cursor={g.is_locked ? "not-allowed" : "pointer"}
                        _hover={g.is_locked ? {} : { borderColor: TOP_BAR_BLUE }}
                      >
                        <Flex justify="flex-end" minH="16px" mb={1}>
                          {g.is_final ? (
                            <Text fontSize="0.6rem" fontWeight="bold" color="gray.700">
                              {g.away_score}-{g.home_score} F
                            </Text>
                          ) : g.is_locked ? (
                            <Badge colorScheme="gray" fontSize="0.5rem">Locked</Badge>
                          ) : null}
                        </Flex>

                        {/* Logos side by side with abbreviations underneath, as
                            on the PL pick page. Away first, matching the NFL
                            convention used everywhere else in the app. */}
                        <Flex justify="center" align="flex-start" gap={1} w="100%">
                          {[g.away_team, g.home_team].map((t, i) => (
                            <React.Fragment key={t?.id || i}>
                              {i === 1 && (
                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.700"
                                      fontWeight="bold" px={1} alignSelf="center">
                                  @
                                </Text>
                              )}
                              <VStack spacing={0} align="center" minW="0">
                                {t?.logo_url && (
                                  <Image src={t.logo_url}
                                         boxSize={{ base: "40px", md: "50px" }}
                                         objectFit="contain" />
                                )}
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  lineHeight="1.3"
                                  textAlign="center"
                                  bg={pick?.picked_team_id === t?.id ? PICK_HIGHLIGHT_BG : "transparent"}
                                  px={1}
                                  borderRadius="sm"
                                  color={usedSet.has(t?.id) ? "gray.400" : "black"}
                                  noOfLines={1}
                                >
                                  {t?.short_name}
                                </Text>
                              </VStack>
                            </React.Fragment>
                          ))}
                        </Flex>

                        <Text fontSize={{ base: "0.55rem", md: "0.65rem" }} color="gray.500"
                              textAlign="center" mt={1}>
                          {fmtKickoff(g.kickoff_utc)}
                        </Text>

                        {isPicked && (
                          <Flex justify="center" mt={1}>
                            <Badge bg={PICK_HIGHLIGHT_BG} color={PICK_HIGHLIGHT_FG} fontSize="0.5rem">
                              Your pick · {pick.confidence_points}
                            </Badge>
                          </Flex>
                        )}
                      </Box>
                    );
                  })}
                </Flex>

                {(weekData?.bye_teams || []).length > 0 && (
                  <Text fontSize={{ base: "0.6rem", md: "xs" }} color="gray.500" mt={4}>
                    * On bye this week:{" "}
                    {weekData.bye_teams.map((t) => t.short_name).join(", ")}
                  </Text>
                )}
              </>
            )}
          </Box>

          {/* Current standings, by division */}
          <Box flex="1" minW={{ base: "100%", md: "360px" }}
               maxW={{ base: "100%", md: "360px" }}
               bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Heading size="md" color={TOP_BAR_BLUE} mb={4}>Current Standings</Heading>
            {divisionGroups.length === 0 ? (
              <Text color="gray.600" fontSize="sm">
                Standings appear once the season starts.
              </Text>
            ) : (
              <Box overflowX="auto" maxH="640px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Team</Th>
                      <Th isNumeric>W</Th>
                      <Th isNumeric>L</Th>
                      <Th isNumeric>T</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {divisionGroups.map((d) => (
                      <React.Fragment key={d.key}>
                        <Tr bg="gray.50">
                          <Td colSpan={4} fontWeight="bold" fontSize="0.65rem"
                              color={TOP_BAR_BLUE}>
                            {d.key}
                          </Td>
                        </Tr>
                        {d.teams.map((row) => (
                          <Tr key={row.team?.id}>
                            <Td>
                              <HStack spacing={2}>
                                {row.team?.logo_url && (
                                  <Image src={row.team.logo_url} boxSize="16px"
                                         objectFit="contain" />
                                )}
                                <Text fontSize="xs">{row.team?.short_name}</Text>
                              </HStack>
                            </Td>
                            <Td isNumeric fontSize="xs">{row.wins}</Td>
                            <Td isNumeric fontSize="xs">{row.losses}</Td>
                            <Td isNumeric fontSize="xs">{row.ties}</Td>
                          </Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
          </Flex>

          {/* ── Pick history ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Heading size="md" color={TOP_BAR_BLUE} mb={4}>Pick History</Heading>
            <Grid templateColumns={{ base: "1fr", md: "repeat(5, minmax(0, 1fr))" }} gap={3}>
              {historyByWeek.map((item) => (
                <GridItem
                  key={`history-${item.week}`}
                  bg="gray.50"
                  border={item.is_auto_pick ? "2px solid" : "1px solid"}
                  borderColor={item.is_auto_pick ? "red.500" : "gray.200"}
                  borderRadius="md"
                  p={2}
                  minH="44px"
                  display="flex"
                  alignItems="center"
                >
                  {(() => {
                    const res = historyResult(item, TOP_BAR_BLUE);
                    return (
                      <Text fontSize="sm" fontWeight="bold" noOfLines={1} color={res.color}>
                        WK{item.week} - {item.team?.short_name || "-"}{res.label}
                      </Text>
                    );
                  })()}
                </GridItem>
              ))}
            </Grid>
          </Box>
        </VStack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
                      borderRadius="lg" maxW="900px" w="calc(100vw - 32px)">
          <ModalHeader color={TOP_BAR_BLUE} pr={14}>
            <Flex align="center" justify="space-between" gap={3}>
              <Box>
                {activeGame
                  ? `${shortTitle ? activeGame.away_team?.short_name : activeGame.away_team?.name} @ ${
                      shortTitle ? activeGame.home_team?.short_name : activeGame.home_team?.name}`
                  : "Make Pick"}
              </Box>
              {activeGame && (
                <Button size="sm" flexShrink={0} bg="red.600" color="white"
                        _hover={{ bg: "red.700" }}
                        isDisabled={!modalTeamId || saving}
                        isLoading={saving}
                        onClick={handleSave}>
                  Save Pick
                </Button>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color={TOP_BAR_BLUE} />

          <ModalBody pb={6}>
            {activeGame ? (
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={3}>Select Winner</Text>
                  <Flex gap={3} flexWrap="wrap" align="flex-start">
                    <TeamChoice
                      team={activeGame.away_team}
                      side="Away"
                      selected={modalTeamId === activeGame.away_team?.id}
                      blockedReason={blockedReason(activeGame, activeGame.away_team?.id)}
                      onSelect={() => { setModalTeamId(activeGame.away_team.id); setModalUnderdog(false); }}
                    />
                    <TeamChoice
                      team={activeGame.home_team}
                      side="Home"
                      selected={modalTeamId === activeGame.home_team?.id}
                      blockedReason={blockedReason(activeGame, activeGame.home_team?.id)}
                      onSelect={() => { setModalTeamId(activeGame.home_team.id); setModalUnderdog(false); }}
                    />
                  </Flex>
                </Box>

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={4}>
                    Confidence Points: {modalConfidence}
                  </Text>
                  <Box px={2}>
                    <Box position="relative" pt={6}>
                      {CONFIDENCE_STEPS.map((num) => (
                        <Text
                          key={`label-${num}`}
                          position="absolute"
                          top="0"
                          left={`${(num - 1) * 25}%`}
                          transform="translateX(-50%)"
                          fontSize="sm"
                          fontWeight={modalConfidence === num ? "bold" : "medium"}
                          color={modalConfidence === num ? TOP_BAR_BLUE : "gray.600"}
                        >
                          {num}
                        </Text>
                      ))}
                      <Slider min={1} max={5} step={1} value={modalConfidence}
                              onChange={setModalConfidence}>
                        <SliderTrack><SliderFilledTrack bg={TOP_BAR_BLUE} /></SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                  </Box>
                </Box>

                {avail.underdog_bonus_enabled && (
                  <Box>
                    {/* Badge sits beside the heading rather than pushed to the
                        far edge, and is colour-coded on whether the bonus can
                        actually be used — not merely on uses remaining, since
                        a use can be left while the bonus is still unavailable. */}
                    <HStack spacing={2} align="center" mb={2}>
                      <Text fontWeight="bold" color={TOP_BAR_BLUE}>
                        Underdog Bonus
                      </Text>
                      <Badge colorScheme={underdogBlockedReason ? "orange" : "blue"}>
                        {underdogUsesLeft} of {avail.underdog_uses_total || 0} left
                      </Badge>
                    </HStack>

                    <Checkbox
                      isChecked={modalUnderdog}
                      isDisabled={!!underdogBlockedReason}
                      onChange={(e) => setModalUnderdog(e.target.checked)}
                      colorScheme="purple"
                    >
                      <Text fontSize="sm">
                        Double my confidence points
                        <Text as="span" fontWeight="bold" color={TOP_BAR_BLUE}>
                          {" "}({modalConfidence} → {modalConfidence * 2})
                        </Text>
                      </Text>
                    </Checkbox>

                    {underdogPoints !== null && (
                      <Text fontSize="xs" color="gray.600" mt={2}>
                        This team is{" "}
                        <Text as="span" fontWeight="bold">
                          {underdogPoints > 0 ? `+${underdogPoints}` : underdogPoints}
                        </Text>{" "}
                        {underdogPoints > 0 ? "underdog" : "favourite"}.
                      </Text>
                    )}
                    <Text fontSize="xs" color={underdogBlockedReason ? "orange.600" : "gray.500"} mt={2}>
                      {underdogBlockedReason ||
                        `Doubles what you stake: +${modalConfidence * 2} on a win,
                         −${modalConfidence * 2} on a loss. The use is spent either way.`}
                    </Text>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={3}>Game Odds</Text>
                  {activeGame.odds ? (
                    <>
                      <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                        {[
                          {
                            label: "Spread",
                            // Signed against home, so negative means the home
                            // side is laying the points.
                            display:
                              activeGame.odds.spread == null
                                ? null
                                : activeGame.odds.spread < 0
                                ? `${activeGame.home_team?.short_name} ${activeGame.odds.spread}`
                                : `${activeGame.away_team?.short_name} -${activeGame.odds.spread}`,
                            american: null,
                          },
                          {
                            label: activeGame.away_team?.short_name,
                            display: activeGame.odds.away_win,
                            american: toAmericanOdds(activeGame.odds.away_win),
                          },
                          {
                            label: activeGame.home_team?.short_name,
                            display: activeGame.odds.home_win,
                            american: toAmericanOdds(activeGame.odds.home_win),
                          },
                        ].map(({ label, display, american }) => (
                          <Box
                            key={label}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            p={3}
                            textAlign="center"
                          >
                            <Text fontSize="xs" color="gray.500" mb={1} noOfLines={1}>
                              {label}
                            </Text>
                            <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} lineHeight="1.2">
                              {display ?? "—"}
                            </Text>
                            {american && (
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {american}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </Grid>
                      <Text fontSize="xs" color="gray.400" mt={2}>
                        {activeGame.odds.bookmaker}
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Odds are published 1–7 days before kickoff.
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" color={TOP_BAR_BLUE} mb={2}>Team Stats</Text>
                  {(() => {
                    const rows = [activeGame.away_team, activeGame.home_team].map((t) => ({
                      team: t,
                      rec: recordByTeam[t?.id],
                    }));
                    if (!rows.some((r) => r.rec)) {
                      return (
                        <Text fontSize="sm" color="gray.500">
                          No records yet — the season hasn&apos;t started.
                        </Text>
                      );
                    }
                    return (
                      <HStack spacing={3} align="stretch">
                        {rows.map(({ team, rec }) => (
                          <Box key={team?.id} flex="1" borderWidth="1px" borderColor="gray.200"
                               borderRadius="md" p={3}>
                            <HStack spacing={2} mb={2}>
                              {team?.logo_url && (
                                <Image src={team.logo_url} boxSize="20px" objectFit="contain" />
                              )}
                              <Text fontSize="sm" fontWeight="bold">{team?.short_name}</Text>
                            </HStack>
                            {rec ? (
                              <VStack align="stretch" spacing={1}>
                                <HStack justify="space-between">
                                  <Text fontSize="0.6rem" color="gray.500">RECORD</Text>
                                  <Text fontSize="xs">
                                    {rec.wins}-{rec.losses}{rec.ties ? `-${rec.ties}` : ""}
                                  </Text>
                                </HStack>
                                <HStack justify="space-between">
                                  <Text fontSize="0.6rem" color="gray.500">PF / PA</Text>
                                  <Text fontSize="xs">{rec.points_for} / {rec.points_against}</Text>
                                </HStack>
                                <HStack justify="space-between">
                                  <Text fontSize="0.6rem" color="gray.500">DIFF</Text>
                                  <Text fontSize="xs"
                                        color={rec.point_diff > 0 ? "green.600" : rec.point_diff < 0 ? "red.600" : "gray.600"}>
                                    {rec.point_diff > 0 ? "+" : ""}{rec.point_diff}
                                  </Text>
                                </HStack>
                                <HStack justify="space-between">
                                  <Text fontSize="0.6rem" color="gray.500">STREAK</Text>
                                  <Text fontSize="xs">{rec.streak || "—"}</Text>
                                </HStack>
                              </VStack>
                            ) : (
                              <Text fontSize="xs" color="gray.500">No record</Text>
                            )}
                          </Box>
                        ))}
                      </HStack>
                    );
                  })()}
                </Box>

                <Box>
                  <MatchupHistoryContent
                    title="Matchup History"
                    teamA={activeGame.away_team}
                    teamB={activeGame.home_team}
                    seasons={5}
                    fetcher={getNflMatchupHistory}
                    weekPrefix="Wk"
                    drawLabel="TIE"
                    active={isOpen}
                    maxH="240px"
                  />
                </Box>
              </VStack>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
